import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import Toast from "@/components/base/Toast";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { useToast } from "@/hooks/useToast";
import { siteIntro } from "@/mocks/news";

const FORM_ID = "db8gd18kk00l6eltrjsg";
const SUBMIT_ADDR = "https://readdy.ai/api/form/db8gd18kk00l6eltrjsg";

const serviceScope = [
  { icon: "ri-film-line", title: "影视资料库", desc: "收录电影、电视剧的基础资料、剧情简介、演职人员与评分参考。" },
  { icon: "ri-compass-3-line", title: "个性化推荐", desc: "根据类型、地区、年份多维筛选，帮你快速定位感兴趣的作品。" },
  { icon: "ri-newspaper-line", title: "影视资讯", desc: "行业动态、追剧指南与深度解读，让你紧跟影视圈最新话题。" },
  { icon: "ri-heart-3-line", title: "本地收藏", desc: "收藏喜欢的作品，保存在本机浏览器中，随时回来继续追。" },
];

export default function About() {
  const { message, visible, show } = useToast();
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  useDocumentMeta({
    title: "关于我们 - 天天影院影视内容平台",
    description:
      "了解天天影院的网站定位、内容服务范围与版权声明，并通过反馈表单与我们联系，一起打造更好的影视内容平台。",
    keywords: "天天影院,关于我们,影视平台,联系我们,意见反馈",
  });

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formEl = e.currentTarget;
    const formData = new FormData(formEl);

    // 反垃圾：蜜罐字段若被填写，则静默按成功处理，不发送任何请求
    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      show("感谢你的反馈，我们会尽快处理");
      formEl.reset();
      return;
    }

    setStatus("submitting");
    setFormError("");

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      if (typeof value === "string" && value.trim() === "") return;
      params.append(key, String(value));
    });

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      const ok = response.ok && parsed?.code === "OK" && !isSpam;

      if (ok) {
        setStatus("success");
        show("反馈已提交，感谢你的支持");
        formEl.reset();
      } else {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg ? serverMsg : "提交失败，请稍后重试"
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试");
    }
  };

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-8 animate-fade-in">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />

      <div className="mt-5 rounded-lg bg-primary-950 px-6 py-9 md:px-10 md:py-12">
        <h1 className="font-heading text-2xl md:text-4xl font-bold text-background-50 mb-3">
          关于天天影院
        </h1>
        <p className="text-background-200/90 text-sm md:text-base max-w-3xl leading-7">
          {siteIntro}
        </p>
      </div>

      <section className="mt-8 grid grid-cols-1 lg:grid-cols-[1.3fr_1fr] gap-6">
        <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
          <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-4 flex items-center gap-2">
            <i className="ri-flag-2-line text-primary-500" />
            我们的使命
          </h2>
          <p className="text-sm md:text-[15px] leading-8 text-foreground-700">
            天天影院希望成为你身边最省心的影视助手。面对海量作品，我们通过清晰的分类、真实的资料与用心的推荐，
            让你不用再为「看什么」而纠结，把时间留给真正的观影与追剧时光。
          </p>

          <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-4 mt-8 flex items-center gap-2">
            <i className="ri-stack-line text-primary-500" />
            内容服务范围
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
            {serviceScope.map((item) => (
              <div key={item.title} className="rounded-lg bg-background-100 p-4">
                <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-700 text-xl mb-3">
                  <i className={item.icon} />
                </span>
                <h3 className="font-medium text-foreground-900 mb-1">{item.title}</h3>
                <p className="text-sm text-foreground-500 leading-6">{item.desc}</p>
              </div>
            ))}
          </div>

          <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-4 mt-8 flex items-center gap-2">
            <i className="ri-copyright-line text-primary-500" />
            版权声明
          </h2>
          <p className="text-sm md:text-[15px] leading-8 text-foreground-700">
            本站所有影视简介、演职人员及资讯内容均为演示用途的虚构示例，不提供任何真实的影视播放、下载与存储服务。
            如本站内容涉及相关权利，请通过下方联系方式与我们取得联系，我们将及时处理。
          </p>
        </div>

        <div>
          <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-7">
            <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-4 flex items-center gap-2">
              <i className="ri-contacts-line text-primary-500" />
              联系方式
            </h2>
            <ul className="space-y-4 text-sm">
              <li className="flex items-center gap-3">
                <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-50 text-primary-600">
                  <i className="ri-mail-line" />
                </span>
                <span className="text-foreground-700">contact@tiantiancinema.com</span>
              </li>
              <li className="flex items-center gap-3">
                <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-50 text-primary-600">
                  <i className="ri-wechat-line" />
                </span>
                <span className="text-foreground-700">微信公众号：天天影院</span>
              </li>
              <li className="flex items-center gap-3">
                <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-50 text-primary-600">
                  <i className="ri-map-pin-line" />
                </span>
                <span className="text-foreground-700">服务时间：每日 09:00 - 22:00</span>
              </li>
            </ul>
            <div className="mt-5 flex items-center gap-3">
              <a
                href="https://destv.cn"
                target="_blank"
                rel="noreferrer"
                className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 text-foreground-700 hover:bg-accent-500 hover:text-background-50 transition-colors cursor-pointer"
                aria-label="微博"
              >
                <i className="ri-weibo-fill" />
              </a>
              <a
                href="https://www.bilibili.com"
                target="_blank"
                rel="noreferrer"
                className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 text-foreground-700 hover:bg-accent-500 hover:text-background-50 transition-colors cursor-pointer"
                aria-label="哔哩哔哩"
              >
                <i className="ri-bilibili-fill" />
              </a>
              <a
                href="https://www.youtube.com"
                target="_blank"
                rel="noreferrer"
                className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 text-foreground-700 hover:bg-accent-500 hover:text-background-50 transition-colors cursor-pointer"
                aria-label="YouTube"
              >
                <i className="ri-youtube-fill" />
              </a>
            </div>
          </div>

          <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-6 md:p-7">
            <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-1 flex items-center gap-2">
              <i className="ri-chat-smile-2-line text-primary-500" />
              意见反馈
            </h2>
            <p className="text-sm text-foreground-500 mb-5">
              你的建议是我们前进的动力，欢迎留言告诉我们。
            </p>

            <form id={FORM_ID} data-readdy-form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="fb-name" className="block text-sm text-foreground-700 mb-1.5">
                  您的称呼
                </label>
                <input
                  id="fb-name"
                  name="name"
                  type="text"
                  required
                  placeholder="请输入您的称呼"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm outline-none transition-colors focus:border-primary-400"
                />
              </div>

              <div>
                <label htmlFor="fb-email" className="block text-sm text-foreground-700 mb-1.5">
                  联系邮箱
                </label>
                <input
                  id="fb-email"
                  name="email"
                  type="email"
                  required
                  placeholder="name@example.com"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm outline-none transition-colors focus:border-primary-400"
                />
              </div>

              <div>
                <label htmlFor="fb-subject" className="block text-sm text-foreground-700 mb-1.5">
                  反馈类型
                </label>
                <select
                  id="fb-subject"
                  name="subject"
                  defaultValue="内容建议"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm outline-none transition-colors focus:border-primary-400 cursor-pointer"
                >
                  <option value="内容建议">内容建议</option>
                  <option value="功能反馈">功能反馈</option>
                  <option value="问题举报">问题举报</option>
                  <option value="商务合作">商务合作</option>
                  <option value="其他">其他</option>
                </select>
              </div>

              <div>
                <label htmlFor="fb-message" className="block text-sm text-foreground-700 mb-1.5">
                  反馈内容
                </label>
                <textarea
                  id="fb-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={4}
                  placeholder="请描述你的想法或遇到的问题（500 字以内）"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm outline-none transition-colors focus:border-primary-400 resize-none"
                />
              </div>

              <input
                className="form-field-aux"
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              {status === "error" && formError && (
                <p className="text-sm text-accent-700 bg-accent-50 border border-accent-200 rounded-md px-3 py-2">
                  {formError}
                </p>
              )}
              {status === "success" && (
                <p className="text-sm text-primary-700 bg-primary-50 border border-primary-200 rounded-md px-3 py-2">
                  反馈已提交成功，感谢你的支持！
                </p>
              )}

              <button
                type="submit"
                disabled={status === "submitting"}
                className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-background-50 text-sm font-medium py-3 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"} />
                {status === "submitting" ? "提交中…" : "提交反馈"}
              </button>
              <p className="text-center text-xs text-foreground-400">
                本表单用于收集你的意见，我们不会向第三方泄露你的信息。
              </p>
            </form>
          </div>

          <div className="mt-6 rounded-lg bg-secondary-100 p-5 md:p-6">
            <p className="text-sm text-secondary-900 leading-7">
              想直接开始找片？前往{" "}
              <Link to="/category/movie" className="text-primary-700 font-medium hover:text-accent-600 cursor-pointer">
                电影频道
              </Link>{" "}
              或{" "}
              <Link to="/category/tv" className="text-primary-700 font-medium hover:text-accent-600 cursor-pointer">
                电视剧频道
              </Link>{" "}
              发现更多好作品。
            </p>
          </div>
        </div>
      </section>

      <Toast message={message} visible={visible} />
    </div>
  );
}